import { FC, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { useEstabrookContext } from "service/api/estabrookContext";
import { usePlowReportDownload } from "../../../hooks/use-plow-report-download";
import { fetchCustomerPlowReport } from "../../../service/plow-report";

const DATE_FORMAT = "MM/dd/yyyy";

export interface PlowReportDialogProps {
  open: boolean;
  onClose: () => void;
  /** Location ids to include: a single row (`[locationId]`) or a bulk selection. */
  locationIds: string[];
  /** Secondary line under the title, e.g. "Greta Halvorsen - 412 Birchwood Ln". */
  subtitle?: string;
  /** Fired after a successful download (before the dialog closes). */
  onDownloaded?: () => void;
  /** Prefill "Season to Date" on open instead of leaving the range blank — for
   *  callers whose trigger is already season-scoped (e.g. the "Skipped orders
   *  per season" card). Defaults to false. */
  defaultToSeasonToDate?: boolean;
}

/**
 * "Season to Date" prefills the range (per Figma annotation):
 *   1. Start = the season start day, Oct 15 (previous year if today is before
 *      Oct 15 of the current year, so the range is never inverted)
 *   2. End = today
 */
const getSeasonToDate = (): { start: DateTime; end: DateTime } => {
  const now = DateTime.now();
  const seasonStart = DateTime.local(now.year, 10, 15);
  const start =
    now < seasonStart ? seasonStart.minus({ years: 1 }) : seasonStart;
  return {
    start,
    end: now.startOf("day"),
  };
};

const PlowReportDialog: FC<PlowReportDialogProps> = ({
  open,
  onClose,
  locationIds,
  subtitle,
  onDownloaded,
  defaultToSeasonToDate = false,
}) => {
  const { fetcherOptions } = useEstabrookContext();
  const { download, isLoading, error, reset } = usePlowReportDownload();

  const [startDate, setStartDate] = useState<DateTime | null>(null);
  const [endDate, setEndDate] = useState<DateTime | null>(null);
  const [seasonApplied, setSeasonApplied] = useState(false);

  // Reset whenever the dialog is (re)opened — prefilled with "Season to Date"
  // for season-scoped callers, blank otherwise. Either way the user can still
  // pick different dates via the pickers below.
  useEffect(() => {
    if (open) {
      if (defaultToSeasonToDate) {
        const { start, end } = getSeasonToDate();
        setStartDate(start);
        setEndDate(end);
        setSeasonApplied(true);
      } else {
        setStartDate(null);
        setEndDate(null);
        setSeasonApplied(false);
      }
      reset();
    }
  }, [open, defaultToSeasonToDate, reset]);

  const handleSeasonToDate = () => {
    const { start, end } = getSeasonToDate();
    setStartDate(start);
    setEndDate(end);
    setSeasonApplied(true);
    reset();
  };

  const handleStartChange = (value: DateTime | null) => {
    setStartDate(value);
    setSeasonApplied(false);
    reset();
  };

  const handleEndChange = (value: DateTime | null) => {
    setEndDate(value);
    setSeasonApplied(false);
    reset();
  };

  /**
   * The pickers only *flag* a bad value (red outline) — `minDate`/`maxDate` gate
   * the calendar, but a date typed straight into the field still lands in state.
   * Validate here so an inverted or unparseable range is caught before the
   * request, instead of surfacing the backend's raw "`from` must be on or before
   * `to`".
   */
  const validationError = ((): string | null => {
    if (!startDate || !endDate) {
      return null;
    }
    if (!startDate.isValid || !endDate.isValid) {
      return "Enter valid dates in MM/DD/YYYY format.";
    }
    if (startDate.startOf("day") > endDate.startOf("day")) {
      return "Start date must be on or before the end date.";
    }
    return null;
  })();

  // Validation is the more actionable message, so it wins over a stale
  // download failure.
  const feedback = validationError ?? error;

  const canDownload =
    Boolean(startDate && endDate) && !validationError && !isLoading;

  const handleDownload = async () => {
    if (!startDate || !endDate) {
      return;
    }
    const from = startDate.toISODate() ?? "";
    const to = endDate.toISODate() ?? "";
    const succeeded = await download(
      () =>
        fetchCustomerPlowReport(fetcherOptions, {
          locations: locationIds,
          from,
          to,
        }),
      // Modal shows loading + inline error itself, so suppress the error toast.
      { withErrorToast: false },
    );
    if (succeeded) {
      onDownloaded?.();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "400px",
            borderRadius: "28px",
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Typography sx={{ fontSize: "22px", lineHeight: "28px" }}>
          Download Plow Report
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            sx={{ display: "block", color: "#C1C7CE", mt: "4px" }}
          >
            {subtitle}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              pt: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="body2" sx={{ color: "#C1C7CE" }}>
                Date Range
              </Typography>
              <Chip
                label="Season to Date"
                onClick={handleSeasonToDate}
                disabled={seasonApplied}
                data-testid="season-to-date"
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <DatePicker
                value={startDate}
                onChange={handleStartChange}
                format={DATE_FORMAT}
                maxDate={endDate ?? undefined}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <ArrowForward fontSize="small" sx={{ color: "#8B9198" }} />
              <DatePicker
                value={endDate}
                onChange={handleEndChange}
                format={DATE_FORMAT}
                minDate={startDate ?? undefined}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Box>
            {feedback && (
              <Box
                data-testid="plow-report-error"
                sx={{
                  width: "100%",
                  backgroundColor: "#FFB4AB",
                  borderRadius: "8px",
                  px: "16px",
                  py: "14px",
                  boxShadow:
                    "0px 4px 4px rgba(0,0,0,0.15), 0px 1px 1.5px rgba(0,0,0,0.3)",
                }}
              >
                <Typography
                  sx={{
                    color: "#690005",
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "0.1px",
                  }}
                >
                  {feedback}
                </Typography>
              </Box>
            )}
          </Box>
        </LocalizationProvider>
      </DialogContent>
      {/* Paper already has 24px padding (theme); keep horizontal padding 0 so the
          Download button's right edge lines up with the date fields above. */}
      <DialogActions sx={{ p: 0, pt: "24px" }}>
        <Button onClick={onClose} data-testid="plow-report-cancel">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleDownload}
          disabled={!canDownload}
          data-testid="plow-report-download"
          startIcon={
            isLoading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlowReportDialog;
